/**
 * Global handler for chunk loading errors
 * This helps handle cases where chunks fail to load due to:
 * - Network issues
 * - Cache mismatches after deployment
 * - Old service workers serving stale code
 */

const RETRY_LIMIT = 3;
const chunkRetries = {};

// Store the original error handler
const originalOnError = window.onerror;

// Override global error handler to catch chunk load errors
window.onerror = function(message, source, lineno, colno, error) {
  // Check if it's a chunk loading error
  if (
    error?.name === 'ChunkLoadError' ||
    (typeof message === 'string' && (
      message.includes('Loading chunk') ||
      message.includes('Loading CSS chunk')
    ))
  ) {
    console.error('Chunk loading error detected:', error);
    
    // Extract chunk ID from error message if possible
    const chunkIdMatch = message.match(/chunk (\d+)/);
    const chunkId = chunkIdMatch ? chunkIdMatch[1] : 'unknown';
    
    // Initialize retry counter for this chunk
    if (!chunkRetries[chunkId]) {
      chunkRetries[chunkId] = 0;
    }
    
    // Increment retry counter
    chunkRetries[chunkId]++;
    
    // If we haven't exceeded retry limit, try to reload
    if (chunkRetries[chunkId] <= RETRY_LIMIT) {
      console.log(`Retry attempt ${chunkRetries[chunkId]} for chunk ${chunkId}`);
      
      // Clear service worker cache and reload
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then(registrations => {
          registrations.forEach(registration => {
            registration.unregister();
          });
          
          // Clear cache and reload
          if ('caches' in window) {
            caches.keys().then(names => {
              names.forEach(name => {
                caches.delete(name);
              });
              window.location.reload(true);
            });
          } else {
            window.location.reload(true);
          }
        });
      } else {
        // Just reload if service worker is not available
        window.location.reload(true);
      }
      
      return true; // Prevent default error handling
    } else {
      console.error(`Chunk ${chunkId} failed to load after ${RETRY_LIMIT} retries`);
      alert('Failed to load application resources. Please clear your browser cache and try again.');
    }
  }
  
  // Call original error handler if it exists
  if (originalOnError) {
    return originalOnError.apply(this, arguments);
  }
  
  return false;
};

// Also handle promise rejections from dynamic imports
window.addEventListener('unhandledrejection', function(event) {
  if (
    event.reason?.name === 'ChunkLoadError' ||
    event.reason?.message?.includes('Loading chunk') ||
    event.reason?.message?.includes('Loading CSS chunk')
  ) {
    console.error('Unhandled chunk loading rejection:', event.reason);
    event.preventDefault();
    
    // Trigger the error handler
    window.onerror(
      event.reason.message,
      null,
      null,
      null,
      event.reason
    );
  }
});

export default {
  resetRetries: () => {
    Object.keys(chunkRetries).forEach(key => {
      delete chunkRetries[key];
    });
  }
};

